import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Step = 'idle' | 'code' | 'password' | 'authorized';

interface Status {
  configured: boolean;
  authorized: boolean;
  user?: { id: string; username?: string; firstName?: string; phone?: string };
}

export function TelegramPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status | null>(null);
  const [step, setStep] = useState<Step>('idle');
  const [phone, setPhone] = useState('+31');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = async () => {
    const s = await api.get<Status>('/api/telegram/status');
    setStatus(s);
    if (s.authorized) setStep('authorized');
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Status laden mislukt'));
  }, [user?.role]);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.post('/api/telegram/login/start', { phone });
      setStep('code');
      setInfo('Code verstuurd via Telegram. Vul die hieronder in.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code versturen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ step: Step }>('/api/telegram/login/code', { code });
      if (res.step === 'password') {
        setStep('password');
        setInfo('Dit account heeft 2FA. Vul je cloud-wachtwoord in.');
      } else {
        setStep('authorized');
        setInfo('Telegram is gekoppeld.');
        await load();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code ongeldig');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/telegram/login/password', { password });
      setStep('authorized');
      setInfo('Telegram is gekoppeld.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA mislukt');
    } finally {
      setBusy(false);
    }
  };

  const logoutTg = async () => {
    if (!confirm('Telegram-sessie verbreken?')) return;
    setBusy(true);
    try {
      await api.post('/api/telegram/logout');
      setStep('idle');
      setCode('');
      setPassword('');
      setInfo('Sessie verwijderd.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uitloggen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    await api.post('/api/telegram/login/cancel');
    setStep('idle');
    setCode('');
    setPassword('');
    setInfo(null);
  };

  return (
    <div>
      <h1>Telegram</h1>
      <p className="page-lead">
        Koppel een Telegram-account in de browser. API-id en hash staan in de Portainer stack-omgeving;
        hier log je alleen interactief in.
      </p>

      <div className="panel">
        <div className="row">
          <strong>Credentials</strong>
          <span className={`badge ${status?.configured ? 'ok' : 'warn'}`}>
            {status?.configured ? 'gezet in stack' : 'TELEGRAM_API_ID/HASH ontbreken'}
          </span>
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <strong>Sessie</strong>
          <span className={`badge ${status?.authorized ? 'ok' : 'warn'}`}>
            {status?.authorized ? 'ingelogd' : 'niet ingelogd'}
          </span>
        </div>
        {status?.authorized && status.user && (
          <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
            {status.user.firstName || 'Account'}
            {status.user.username ? ` (@${status.user.username})` : ''}
            {status.user.phone ? ` · +${status.user.phone}` : ''}
          </p>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert ok">{info}</div>}

      {status?.authorized ? (
        <div className="panel" style={{ marginTop: 14 }}>
          <p className="muted">Worker gebruikt deze sessie automatisch voor inladen en ontdekken.</p>
          <button className="btn btn-danger" type="button" disabled={busy} onClick={() => void logoutTg()}>
            Telegram uitloggen
          </button>
        </div>
      ) : (
        <div className="panel" style={{ marginTop: 14 }}>
          {step === 'idle' && (
            <form className="form-col" onSubmit={(e) => void start(e)}>
              <div className="field">
                <label htmlFor="phone">Telefoonnummer</label>
                <input
                  id="phone"
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+31612345678"
                  required
                  disabled={!status?.configured || busy}
                />
              </div>
              <button className="btn" type="submit" disabled={!status?.configured || busy}>
                {busy ? 'Bezig…' : 'Stuur login-code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form className="form-col" onSubmit={(e) => void submitCode(e)}>
              <div className="field">
                <label htmlFor="code">Login-code uit Telegram</label>
                <input
                  id="code"
                  className="input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="12345"
                  autoFocus
                  required
                  disabled={busy}
                />
              </div>
              <div className="row">
                <button className="btn" type="submit" disabled={busy}>
                  {busy ? 'Bezig…' : 'Bevestigen'}
                </button>
                <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => void cancel()}>
                  Annuleren
                </button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form className="form-col" onSubmit={(e) => void submitPassword(e)}>
              <div className="field">
                <label htmlFor="password">2FA-wachtwoord</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  required
                  disabled={busy}
                />
              </div>
              <div className="row">
                <button className="btn" type="submit" disabled={busy}>
                  {busy ? 'Bezig…' : 'Inloggen'}
                </button>
                <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => void cancel()}>
                  Annuleren
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
